// ------------------------------------------------------------------------------
// ReplaceUploader
// author: mudas
// created: 2025/8/15
// ------------------------------------------------------------------------------

import React, { FC, PropsWithChildren, ReactNode, useRef } from 'react';
import { isNil } from 'lodash-es';
import { useControllableValue } from 'ahooks';
import { useComputed } from 'common/hooks';
import { isImageFile, matchMimeType, normalizeMimeType } from 'common/utils';
import { checkOcr, OcrType } from 'common/helper/ocr';
import { UploadManager } from 'common/hooks/upload/UploadManager';

import { FileACL, UploadFileData, UploadFileItem } from 'common/types';
import { RcFile } from 'antd/es/upload';

import { App, Image, Progress, Upload, UploadFile, UploadProps } from '@fuxi/eevee-ui';
import { Loading3QuartersOutlined, PlusOutlined } from '@ant-design/icons';

import IllegalPNG from 'common/assets/image/illegal.png';

export interface IProps extends Omit<UploadProps<UploadFileData>, 'onChange' | 'itemRender' | 'progress'> {
  // 限制上传文件大小（默认：100MB，单位：MB）
  fileSize?: number;

  // 限制上传文件类型，支持多种格式： txt、.txt、text/plain
  extensions?: string[];

  // 自定义进度条
  progress?: (file: UploadFileItem) => ReactNode;

  // 单个文件的渲染方法
  itemRender?: (file: UploadFileItem) => ReactNode;

  value?: UploadFile<UploadFileData>[];
  onChange?: (value?: UploadFile<UploadFileData>[]) => void;
}

const ReplaceRender = ({
  file,
  progress,
  itemRender,
}: {
  file?: UploadFileItem;
  progress?: IProps['progress'];
  itemRender?: IProps['itemRender'];
}) => {
  const { uid, status, thumbUrl, percent = 0 } = file ?? {};

  // 若设置了上传状态，则计算是否上传完成
  const ready = useComputed<boolean>(() => {
    if (isNil(percent) && isNil(status)) return true;
    return percent === 100 && status === 'done';
  }, [percent, status]);

  const itemNode = itemRender ? (
    itemRender(file!)
  ) : (
    <Image
      key={uid}
      preview={false}
      src={thumbUrl?.url!}
      placeholder={
        <div className="w-full h-full flex-center">
          <Loading3QuartersOutlined spin className="text-size-6 color-primary opacity-50" />
        </div>
      }
      className={`w-30! h-30! rounded-xl bg-container object-cover
      b-1 b-solid border-color-normal ${file?.illegal ? 'grayscale-100 opacity-80 ' : ''}`}
    />
  );

  const previewNode = progress ? (
    progress(file!)
  ) : (
    <div className="w-30! h-30! rounded-xl bg-container b-1 b-dashed border-color-normal flex-center">
      <Progress
        type="circle"
        size={60}
        percent={percent}
        className="opacity-60"
        format={() => <span className="text-size-xs">上传中</span>}
      />
    </div>
  );

  return (
    <div className="relative">
      {ready ? itemNode : previewNode}
      {file?.illegal && (
        <Image wrapperClassName="absolute right-2% bottom-2% w-60% h-60%" preview={false} src={IllegalPNG} />
      )}
    </div>
  );
};

const ReplaceUploader: FC<PropsWithChildren<IProps>> = (props) => {
  const { fileSize = 10, extensions, value, progress, itemRender, children, onChange, ...otherProps } = props;

  const um = useRef<UploadManager>(new UploadManager({ autoUpload: true }));

  const { message } = App.useApp();
  const [files, setFiles] = useControllableValue<UploadFileItem[]>(props, { defaultValue: [] });

  const mimeTypes = useComputed(() => {
    return Array.isArray(extensions) ? normalizeMimeType(extensions).join(',') : '';
  }, [extensions]);

  const validFiles = useComputed<UploadFileItem[]>(() => {
    return files?.filter((item) => item.status !== 'error') ?? [];
  }, [files]);

  // --------------------------------------------------------------------------
  //
  // Class methods
  //
  // --------------------------------------------------------------------------

  const beforeUpload = async (file: UploadFile, fileList: UploadFile[]) => {
    if (!file) {
      return Upload.LIST_IGNORE;
    }

    // // 限制文件数量
    // if ((files?.length ?? 0) + (fileList?.length ?? 0) > limit) {
    //   message.error(`最多上传 ${limit} 个文件`);
    //   return Upload.LIST_IGNORE;
    // }

    if (Array.isArray(extensions) && extensions.length > 0 && !matchMimeType(file.name, extensions)) {
      message.error(`不支持上传该格式文件`);
      return Upload.LIST_IGNORE;
    }

    if ((file.size ?? 0) / (1024 * 1024) > fileSize) {
      message.error(`请上传 ${fileSize}MB 以内的文件`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const customRequest: UploadProps['customRequest'] = ({ file, onProgress, onSuccess, onError }) => {
    um.current.add(file as RcFile, {
      params: {
        acl: FileACL.Public,
      },
      onFinished: (data, file) => {
        onSuccess?.({ data, status: 'success' });
      },
      onFailed: (error) => {
        onError?.(new Error(error.message));
      },
      onProgress: (percent) => {
        onProgress?.({ percent });
      },
    });
  };

  // --------------------------------------------------------------------------
  //
  // Event handlers
  //
  // --------------------------------------------------------------------------

  const handleChange = async ({ file, fileList }) => {
    const { uid, name, status, percent, error, response } = file;

    // 错误的配置值可能导致 files 异常（如 DSL 中将默认值置为 ""）
    const curFiles = Array.isArray(files) ? files : [];

    // uid 与 url.fileId 皆可做唯一检测
    let target = curFiles?.find((item) => item.uid === uid);
    const type = isImageFile(name) ? OcrType.Image : OcrType.Video;

    // 替换
    if (!target) {
      target = { uid, name, type, percent: 0, status, illegal: false };
    }

    target.percent = percent ?? 0;
    target.status = status;
    target.error = error;

    if (status === 'done') {
      const { fileId, url, downloadUrl } = response.data!;
      const illegal = await checkOcr(response.data, type);

      // 接口返回为 true 时，代表通过，此要与 illegal 意义相反
      target.illegal = !illegal;
      target.thumbUrl = {
        fileId,
        url: url || downloadUrl,
      };
      target.url = {
        fileId,
        url: url || downloadUrl,
      };
    }

    setFiles([target]);
  };

  return (
    <Upload<UploadFileData>
      {...otherProps}
      accept={mimeTypes}
      showUploadList={false}
      beforeUpload={beforeUpload}
      customRequest={customRequest}
      onChange={handleChange}>
      {validFiles?.length ? (
        <div className="flex flex-wrap gap-2">
          {validFiles.map((file) => (
            <ReplaceRender key={file.uid} file={file} progress={progress} itemRender={itemRender} />
          ))}
        </div>
      ) : (
        children || (
          <div
            className="w-30 h-30 pt-4 cursor-pointer
            b-1 b-dashed border-color-normal hover:border-color-primary
            flex flex-col items-center justify-center gap-3
            fill-bg-content rounded-lg text-color-tertiary hover:text-color-primary">
            <PlusOutlined className="text-size-lg" />
            <span>上传图片</span>
          </div>
        )
      )}
    </Upload>
  );
};

export default ReplaceUploader;
